// ─── Admin Dashboard — Supabase Client ──────────────────────────────────────
// Direct Supabase connection for all CRUD operations.
// RLS policies on the master DB restrict access to platform admins.
// No Express API needed — Supabase IS the API.

import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ─── Auth ───────────────────────────────────────────────────────────────────

export const auth = {
  signIn: (email, password) => supabase.auth.signInWithPassword({ email, password }),
  signOut: () => supabase.auth.signOut(),
  getSession: () => supabase.auth.getSession(),
  onAuthStateChange: (cb) => supabase.auth.onAuthStateChange(cb),
};

// ─── Accounts ───────────────────────────────────────────────────────────────
// All queries go directly to Supabase. RLS ensures only platform admins see data.

export const accounts = {
  list: async ({ status, plan, search } = {}) => {
    let query = supabase
      .from("accounts")
      .select("*, billing(*)")
      .order("created_at", { ascending: false });

    if (status) query = query.eq("status", status);
    if (plan) query = query.eq("plan", plan);
    if (search) query = query.or(`name.ilike.%${search}%,slug.ilike.%${search}%`);

    return query;
  },

  get: (id) =>
    supabase
      .from("accounts")
      .select("*, billing(*), account_users(*)")
      .eq("id", id)
      .single(),

  create: (data) =>
    supabase.from("accounts").insert(data).select().single(),

  update: (id, data) =>
    supabase
      .from("accounts")
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single(),

  suspend: (id) =>
    supabase
      .from("accounts")
      .update({ status: "suspended", suspended_at: new Date().toISOString() })
      .eq("id", id),

  activate: (id) =>
    supabase
      .from("accounts")
      .update({ status: "active", activated_at: new Date().toISOString(), suspended_at: null })
      .eq("id", id),
};

// ─── Dashboard Data ─────────────────────────────────────────────────────────

export const dashboard = {
  getStats: async () => {
    const [accountsRes, billingRes] = await Promise.all([
      supabase.from("accounts").select("id, status, plan"),
      supabase.from("billing").select("mrr_cents, status").eq("status", "active"),
    ]);

    const accts = accountsRes.data || [];
    const totalMRR = (billingRes.data || []).reduce((s, b) => s + b.mrr_cents, 0);

    return {
      accounts: {
        total: accts.length,
        active: accts.filter((a) => a.status === "active").length,
        onboarding: accts.filter((a) => a.status === "onboarding").length,
      },
      mrr: totalMRR / 100,
    };
  },

  getEvents: (limit = 20) =>
    supabase
      .from("platform_events")
      .select("*, accounts(name, slug)")
      .order("created_at", { ascending: false })
      .limit(limit),

  getUsage: (accountId) =>
    supabase
      .from("usage_snapshots")
      .select("*")
      .eq("account_id", accountId)
      .order("period_start", { ascending: false })
      .limit(30),
};

// ─── Users ──────────────────────────────────────────────────────────────────

export const users = {
  listForAccount: (accountId) =>
    supabase
      .from("account_users")
      .select("*")
      .eq("account_id", accountId)
      .order("invited_at"),

  invite: (accountId, email, role = "admin") =>
    supabase.from("account_users").insert({ account_id: accountId, email, role }),
};

// ─── Billing ───────────────────────────────────────────────────────────────

export const billing = {
  getForAccount: (accountId) =>
    supabase.from("billing").select("*").eq("account_id", accountId).single(),

  update: (accountId, data) =>
    supabase.from("billing").update({ ...data, updated_at: new Date().toISOString() }).eq("account_id", accountId).select().single(),

  listAll: () =>
    supabase.from("billing").select("*, accounts(name, slug, status)").order("updated_at", { ascending: false }),

  applyDiscount: (accountId, percent, note) =>
    supabase.from("billing").update({ discount_percent: percent, discount_note: note, updated_at: new Date().toISOString() }).eq("account_id", accountId),

  setConversationLimit: (accountId, limit) =>
    supabase.from("billing").update({ conversation_limit: limit, updated_at: new Date().toISOString() }).eq("account_id", accountId),

  resetUsage: (accountId) =>
    supabase.from("billing").update({ conversations_used: 0, conversations_reset_at: new Date().toISOString(), updated_at: new Date().toISOString() }).eq("account_id", accountId),
};

// ─── Provisioning ───────────────────────────────────────────────────────────
// This calls a Vercel serverless function (not Supabase) because it needs to
// orchestrate the Supabase Management API + Stripe — things that require
// server-side secrets that shouldn't be in the browser.

export const provisioning = {
  trigger: async (accountId) => {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/api/provision", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token}`,
      },
      body: JSON.stringify({ account_id: accountId }),
    });
    return res.json();
  },

  getJobs: () =>
    supabase
      .from("provisioning_jobs")
      .select("*, accounts(name, slug)")
      .order("created_at", { ascending: false })
      .limit(20),
};
